// Merges the Executive / CFO / Team dashboards into one route with
// permission-filtered tabs, per the approved restructure plan. The existing
// dashboard components are mounted unmodified as tab panels — this file only
// decides which tabs are visible and does not duplicate their internals.
//
// Gating mirrors the existing dual system exactly (see App.jsx FeatureGate +
// isPathAllowedForRole, and sidenav.jsx's getVisibleNavItems):
//   - role "client" (tenant owner): gated by billing feature-unlocks
//     (GET /api/features), same as FeatureGate does for /analytics and /cfo-dashboard.
//   - every other role (ceo, cfo, employee, manager, analytics_viewer):
//     FeatureGate bypasses them entirely (ROLE_BYPASS_FEATURE_GATE) — they're
//     gated purely by allowedViews, same as isPathAllowedForRole checks today.
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getRole } from "../../utils/authService";
import { apiGet } from "../../utils/api";
import { Tabs } from "../common/ui.jsx";
import AnalyticsDashboard from "../analytics/AnalyticsDashboard.jsx";
import CFODashboard from "../analytics/CFODashboard.jsx";
import RiskCompliance from "../analytics/RiskCompliance.jsx";
import ManagerDashboard from "../analytics/ManagerDashboard.jsx";
import EmployeeDashboard from "../analytics/EmployeeDashboard.jsx";

function getAllowedViewSet() {
  try {
    const raw = JSON.parse(sessionStorage.getItem("allowedViews") || "[]");
    return new Set(Array.isArray(raw) ? raw : []);
  } catch {
    return new Set();
  }
}

export default function DashboardHub() {
  const role = useMemo(() => getRole(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const [unlockedFeatures, setUnlockedFeatures] = useState({});
  const [loaded, setLoaded] = useState(role !== "client"); // only client needs the billing fetch

  useEffect(() => {
    if (role !== "client") return;
    let isMounted = true;
    apiGet("/api/features")
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted) return;
        const map = {};
        (data?.unlockedFeatures || []).forEach((id) => { map[id] = true; });
        setUnlockedFeatures(map);
      })
      .catch(() => {})
      .finally(() => { if (isMounted) setLoaded(true); });
    return () => { isMounted = false; };
  }, [role]);

  const allowedViewSet = useMemo(() => getAllowedViewSet(), []);

  const tabs = useMemo(() => {
    const list = [];
    const canSee = (featureId, viewKey, clientDefault = false) => {
      if (role === "admin") return true;
      if (role === "client") return clientDefault || !!unlockedFeatures[featureId];
      return allowedViewSet.has(viewKey);
    };

    if (canSee("analytics", "executive_dashboard")) {
      list.push({ id: "overview", label: "Overview", Component: AnalyticsDashboard });
    }
    if (canSee("cfo_dashboard", "cfo_dashboard")) {
      list.push({ id: "cfo", label: "Finance", Component: CFODashboard });
    }
    // Risk & Compliance was formerly a sub-tab inside Overview — same audience
    // as Overview, so it shares Overview's gate rather than getting its own.
    if (canSee("analytics", "executive_dashboard")) {
      list.push({ id: "risk", label: "Risk & Compliance", Component: RiskCompliance });
    }
    // Team tab is for an actual manager's own dashboard, not shown to the
    // tenant owner (client) — a manager needs somewhere to land, but the
    // client already gets Overview+CFO and doesn't need a third, narrower
    // team view mixed in alongside them.
    if (allowedViewSet.has("manager_dashboard")) {
      list.push({ id: "team", label: "Team", Component: ManagerDashboard });
    }
    // "My Work" is a personal contributor view — meaningful for an employee,
    // not for the tenant owner, so it's intentionally not offered to client.
    if (allowedViewSet.has("employee_dashboard")) {
      list.push({ id: "mywork", label: "My Work", Component: EmployeeDashboard });
    }
    return list;
  }, [role, unlockedFeatures, allowedViewSet]);

  const requestedTab = searchParams.get("tab");
  const activeTab = tabs.find((t) => t.id === requestedTab) ? requestedTab : tabs[0]?.id;

  if (!loaded) return null;

  if (tabs.length === 0) {
    return (
      <div className="gc-empty">
        <p>No dashboard is available for your account yet. Contact your tenant admin.</p>
      </div>
    );
  }

  const ActivePanel = tabs.find((t) => t.id === activeTab)?.Component || tabs[0].Component;

  return (
    <div>
      <Tabs
        tabs={tabs}
        activeId={activeTab}
        onChange={(id) => setSearchParams(id === tabs[0].id ? {} : { tab: id })}
      />
      <ActivePanel />
    </div>
  );
}
